import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import Circle from '@mui/icons-material/Circle';
import { useNotification } from '../../../../../hooks/NotificationContext';
import { getConfigurations } from '../../../../../modules/config';
import {
  fetchExecutionTestSteps,
  updateSingleScript,
  bulkUpdateExecutionStep,
} from '../../../../../modules/executions';
import TestStepItem from './TestStepItem';
import Modal from '../../../../../ui/layout/Modal';
import DetailsCard from '../../../../../ui/layout/DetailsCard';   // ← adjust path to match your project
import CustomSelect from '../../../../../ui/primitives/CustomSelect'; // ← adjust path to match your project
import ActionButton from '../../../../../ui/primitives/ActionButton';        // ← adjust path to match your project
import { isExecutiveManager } from '../../../../../lib/userUtils';
import IssueLinkManager from '../../../../elements/common/traceability/IssueLinkManager';
import { tokens } from '../../../../../mui-theme'; // ← adjust path to match your project

// ─── Column definitions ───────────────────────────────────────────────────────
const BASE_COLUMNS = [
  { label: 'Step',            minWidth: 48  },
  { label: 'Description',     minWidth: 180 },
  { label: 'Test Data',       minWidth: 130 },
  { label: 'Expected Result', minWidth: 180 },
];
const URI_COLUMN   = { label: 'URI',           minWidth: 100 };
const TAIL_COLUMNS = [
  { label: 'Actual Result',   minWidth: 180 },
  { label: 'Status',          minWidth: 160 },
  { label: '',                minWidth: 48  }, // attachments icon
];

// ─────────────────────────────────────────────────────────────────────────────
const TestStepsComponent = ({
  data,
  execution,
  error,
  fetchSelectedExecutionDetails,
  projectId,
  isTestCycleCompleted,
}) => {
  const { addNotification } = useNotification();

  const [testSteps,    setTestSteps]    = useState([]);
  const [statuses,     setStatuses]     = useState([]);
  const [formData,     setFormData]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [showModal,    setShowModal]    = useState(false);
  const [selected,     setSelected]     = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkStatus,   setBulkStatus]   = useState('');
  const [openIssueLinkModal, setOpenIssueLinkModal] = useState(false);
  const [failedStep,   setFailedStep]   = useState(null);

  const defaultStatus  = statuses?.find((s) => s?.is_default);
  const currentUser    = JSON.parse(localStorage.getItem('user'));
  const assignedTo     = execution?.executed_by;
  const technologyLayer = execution?.association?.testcase?.technology_layer;
  const isAssignedToMe = assignedTo === currentUser?.id;

  const isExecutionEditable =
    execution?.is_editable &&
    data?.status_details?.name === 'Approved' &&
    isAssignedToMe &&
    !isExecutiveManager();

  // Build column list once per render (includes URI column when applicable)
  const tableColumns = [
    ...BASE_COLUMNS,
    ...(technologyLayer === 'API/Integration' ? [URI_COLUMN] : []),
    ...TAIL_COLUMNS,
  ];

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchStatuses = async () => {
    try {
      const [testStepsData, statusResponse] = await Promise.all([
        await fetchExecutionTestSteps(execution?.id),
        await getConfigurations('testexecutionstatus', projectId),
      ]);
      setTestSteps(testStepsData?.length > 0 ? testStepsData : []);
      if (statusResponse) setStatuses(statusResponse.data);
    } catch (err) {
      console.error('Error fetching statuses:', err.message);
    }
  };

  useEffect(() => {
    if (execution?.id) fetchStatuses();
  }, [execution]);

  useEffect(() => {
    const sortedTestSteps = [...testSteps].sort(
      (a, b) => a?.teststeps?.s_no - b?.teststeps?.s_no,
    );
    const initialFormData = sortedTestSteps.map((step) => ({
      ...step,
      uri: step?.uri,
      actualResult: step.actual_result || '',
      status: step.status_details ? step.status_details.id : defaultStatus?.id,
    }));
    setFormData(initialFormData);
  }, [execution, statuses, testSteps, defaultStatus]);

  // ── Step update ─────────────────────────────────────────────────────────────
  const handleUpdateStep = async (values, id) => {
    setLoading(true);
    try {
      await updateSingleScript(id, { execution: values });
      addNotification('success', 'Test steps updated successfully!');
      fetchSelectedExecutionDetails(execution?.id);
    } catch (err) {
      if (err.data?.error?.refresh_required) {
        fetchSelectedExecutionDetails(execution?.id);
        addNotification('danger', err?.message || 'Failed to update test steps.');
      } else {
        addNotification('danger', 'Failed to update test steps.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Modal open/close ────────────────────────────────────────────────────────
  const handleShowModal  = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setSelected([]);
    setSelectedRows([]);
    setBulkStatus('');
  };

  // ── Row selection ───────────────────────────────────────────────────────────
  const handleRowSelect = (id) => {
    const idx = selected.indexOf(id);
    let newSelected;
    if      (idx === -1)                    newSelected = selected.concat(id);
    else if (idx === 0)                     newSelected = selected.slice(1);
    else if (idx === selected.length - 1)  newSelected = selected.slice(0, -1);
    else                                    newSelected = [...selected.slice(0, idx), ...selected.slice(idx + 1)];
    setSelected(newSelected);
    setSelectedRows(formData.filter((r) => newSelected.includes(r.id)));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const all = formData.map((r) => r.id);
      setSelected(all);
      setSelectedRows(formData);
    } else {
      setSelected([]);
      setSelectedRows([]);
    }
  };

  const isAllSelected =
    formData.length > 0 &&
    selected.length > 0 &&
    formData.every((r) => selected.includes(r.id));

  // ── Bulk update ─────────────────────────────────────────────────────────────
  const handleSaveClick = async () => {
    if (!bulkStatus || selectedRows.length === 0) return;
    setLoading(true);
    const payload = {
      executionId: execution?.id,
      statusId:    bulkStatus,
      testStepIds: selectedRows.map((r) => r.id),
    };
    try {
      await bulkUpdateExecutionStep(payload);
      addNotification('success', 'Bulk update successful!');
      fetchSelectedExecutionDetails(execution?.id);
      setSelected([]);
      setSelectedRows([]);
      setBulkStatus('');
    } catch (err) {
      if (err.data?.error?.refresh_required) {
        fetchSelectedExecutionDetails(execution?.id);
        addNotification('danger', err?.message || 'Failed to update test steps.');
      } else {
        addNotification('danger', 'Failed to update test steps.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelected([]);
    setSelectedRows([]);
    setBulkStatus('');
  };

  // Clear bulk status whenever fewer than 2 rows are selected
  useEffect(() => {
    if (selected.length < 2) setBulkStatus('');
  }, [selected]);

  const handleStepFailed = (step) => {
    setFailedStep(step);
    setOpenIssueLinkModal(true);
  };

  // renderValue for the bulk-status <CustomSelect> — shows dot + label
  const renderBulkStatusValue = (val) => {
    const s = statuses.find((st) => st.id === val);
    if (!s) return 'Select Status';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Circle sx={{ color: s.color, fontSize: '0.875rem' }} />
        {s.name}
      </Box>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Card shown on the execution detail page ── */}
      <DetailsCard title="Test Steps" contentSx={{ textAlign: 'center' }}>
        {error && (
          <Typography color="error.main" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {testSteps?.length > 0 ? (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Click on the button below to review or begin executing the test steps.
            </Typography>
            {/* ActionButton replaces react-bootstrap <Button variant="primary"> */}
            <ActionButton
              label="Execute"
              intent="primary"
              onClick={handleShowModal}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No test steps found
          </Typography>
        )}
      </DetailsCard>

      {/* ── Execution modal ── */}
      <Modal
        modalTitle="Execute Test Steps"
        open={showModal}
        handleClose={handleCloseModal}
        width="xl"
      >
        <Box sx={{ minHeight: 350 }}>
          {/* Test case title */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            {data.key} : {data.summary}
          </Typography>

          {/* ── Bulk-edit controls (visible only when execution is editable) ── */}
          {isExecutionEditable && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
              {/* Hint: select 2+ rows to unlock bulk editing */}
              {formData.length > 1 && selected.length < 2 && (
                <Box
                  sx={{
                    width: '100%',
                    p: 1,
                    mt: 1,
                    borderLeft: '4px solid',
                    borderColor: 'text.disabled',
                    bgcolor: 'grey.100',
                    borderRadius: '0 4px 4px 0',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Note: Select two or more test steps for bulk editing options.
                  </Typography>
                </Box>
              )}

              {/* Bulk-status selector + Save/Cancel — shown when 2+ rows selected */}
              {selected.length > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    mt: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {/* CustomSelect replaces react-bootstrap Dropdown */}
                  <Box sx={{ width: 210 }}>
                    <CustomSelect
                      id="bulk-status"
                      value={bulkStatus}
                      onChange={(value) => setBulkStatus(value)}
                      options={statuses}
                      placeholder="Select Status"
                      disabled={loading}
                      renderValue={renderBulkStatusValue}
                      renderOption={(opt) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Circle sx={{ color: opt.color, fontSize: '0.875rem' }} />
                          {opt.name}
                        </Box>
                      )}
                    />
                  </Box>

                  {/* ActionButton replaces MuiButton variant="outlined" color="primary" */}
                  <ActionButton
                    label="Save"
                    intent="secondary"
                    onClick={handleSaveClick}
                    disabled={loading || !bulkStatus}
                    loading={loading}
                  />

                  {/* ActionButton replaces MuiButton variant="outlined" color="secondary" */}
                  <ActionButton
                    label="Cancel"
                    intent="ghost"
                    onClick={handleCancel}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* ── Selection badge — mirrors TableComponent's pattern ── */}
          {selected.length > 0 && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 0.5,
                mb: 1.5,
                borderRadius: 1,
                backgroundColor: alpha(tokens.colors.primary[500], 0.07),
                border: `1px solid ${alpha(tokens.colors.primary[500], 0.18)}`,
              }}
            >
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {selected.length}{' '}
                {selected.length === 1 ? 'record' : 'records'} selected
              </Typography>
              <IconButton
                size="small"
                title="Clear Selection"
                onClick={handleCancel}
                sx={{
                  border: '1px solid',
                  borderColor: 'error.main',
                  color: 'error.main',
                  padding: '2px',
                  '&:hover': {
                    backgroundColor: alpha(tokens.colors.error.main, 0.06),
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          )}

          {/* ── Table — replaces <table className="table table-bordered"> ── */}
          {/*
            TableContainer + Table use the theme's MuiTableContainer / MuiTable
            overrides automatically, giving the same look as TableComponent.
            stickyHeader keeps column headers visible while scrolling.
          */}
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader aria-label="test steps table">
              <TableHead>
                <TableRow>
                  {/* Select-all checkbox — replaces <Form.Check type="checkbox"> */}
                  {isExecutionEditable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isAllSelected}
                      indeterminate={selected.length > 0 && !isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      inputProps={{ 'aria-label': 'select all steps' }}
                    />
                  </TableCell>
                  )}

                  {tableColumns.map((col, idx) => (
                    <TableCell key={idx} sx={{ minWidth: col.minWidth }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {formData?.map((script, index) => (
                  <TestStepItem
                    key={script.id}
                    script={{ ...script, onStepFailed: () => handleStepFailed(script) }}
                    index={index}
                    isTestCycleCompleted={isTestCycleCompleted}
                    statuses={statuses}
                    defaultStatus={defaultStatus}
                    isEditing={isExecutionEditable}
                    handleUpdateStep={handleUpdateStep}
                    loading={loading}
                    showUri={technologyLayer === 'API/Integration'}
                    isSelected={selected.includes(script.id)}
                    onSelectRow={(id) => handleRowSelect(id)}
                    isRowDisabled={(row) => !!row.isRowDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>

      {/* ── Issue link modal (unchanged) ── */}
      <IssueLinkManager
        open={openIssueLinkModal}
        onClose={() => setOpenIssueLinkModal(false)}
        entityId={execution?.id}
        entityType="Execution"
        data={data}
        entityKey={data?.key}
        failedStep={failedStep}
      />
    </>
  );
};

export default TestStepsComponent;